import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronsUpDown, KeyRound, LogOut } from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { logout } from '@/api/auth';
import { isApiError } from '@/api/errors';
import type { AdminRole } from '@/api/types';
import { setSession, useCan, useSession } from '@/auth/session';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { NAV_SECTIONS, type NavItem } from './nav';

const ROLE_LABELS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super admin',
  OPERATIONS: 'Operations',
  BILLING: 'Billing',
  READ_ONLY: 'Read only',
};

export function AppSidebar() {
  const can = useCan();
  const { session } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const endSession = () => {
    setSession(queryClient, null);
    void navigate('/login', { replace: true });
  };

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: endSession,
    onError: (error) => {
      // Already signed out on the server: all that is left is to forget it here.
      if (isApiError(error) && error.isUnauthorized) endSession();
    },
  });

  const sections = (session?.must_change_password ? [] : NAV_SECTIONS)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => can(item.permission)),
    }))
    .filter((section) => section.items.length > 0);

  const isNavActive = (item: NavItem) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

  return (
    <Sidebar>
      <SidebarHeader>
        <Link to="/" className="flex flex-col gap-0.5 rounded-md px-2 py-1.5">
          <span className="text-sm font-semibold text-sidebar-foreground">WaSparks Social</span>
          <span className="text-xs text-sidebar-foreground/70">Platform admin</span>
        </Link>
      </SidebarHeader>

      <SidebarContent role="navigation" aria-label="Main">
        {sections.map((section, index) => (
          <SidebarGroup key={section.label ?? index}>
            {section.label && <SidebarGroupLabel>{section.label}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton asChild isActive={isNavActive(item)}>
                      <NavLink to={item.to} end={item.end ?? false}>
                        {item.label}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {session && (
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg" data-testid="account-menu-trigger">
                    <Avatar className="rounded-md">
                      <AvatarFallback className="rounded-md">
                        {initials(session.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left leading-tight">
                      <span className="truncate font-medium">{session.email}</span>
                      <span className="truncate text-xs text-sidebar-foreground/70">
                        {ROLE_LABELS[session.role]}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/account/password">
                      <KeyRound />
                      Change password
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => signOut.mutate()} disabled={signOut.isPending}>
                    <LogOut />
                    {signOut.isPending ? 'Signing out…' : 'Sign out'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
          {signOut.isError && !(isApiError(signOut.error) && signOut.error.isUnauthorized) && (
            <p className="px-2 text-xs text-destructive" role="alert">
              Sign-out failed. Try again.
            </p>
          )}
        </SidebarFooter>
      )}
    </Sidebar>
  );
}

function initials(email: string): string {
  const name = email.split('@')[0] ?? email;
  const parts = name.split(/[._-]/).filter(Boolean);
  const chars = parts.length > 1 ? [parts[0]?.[0], parts[1]?.[0]] : [name[0]];
  return chars.filter(Boolean).join('').toUpperCase();
}
